import { useEffect, useRef } from "react";
import { drawSlamSparks, spawnCardEdgeSparks, stepSlamSparks, type SlamSpark } from "./playSlamSpawn.js";
import type { PlayRarity } from "./playSlamRarity.js";

const IMPACT_MS = 110;

type UsedPlayFxProps = {
  rarities: readonly PlayRarity[];
};

function relativeBox(card: Element, canvas: HTMLCanvasElement) {
  const a = card.getBoundingClientRect();
  const b = canvas.getBoundingClientRect();
  return { x: a.left - b.left, y: a.top - b.top, width: a.width, height: a.height };
}

function fitCanvas(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, host: HTMLElement) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = Math.max(1, host.clientWidth);
  const h = Math.max(1, host.clientHeight);
  if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  return { w, h };
}

export function UsedPlayFx({ rarities }: UsedPlayFxProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const playKey = rarities.join(",");

  useEffect(() => {
    if (!playKey) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !host || !ctx) return;

    const sparks: SlamSpark[] = [];
    let spawned = false;
    let last = performance.now();
    const t0 = last;
    let raf = 0;
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const { w, h } = fitCanvas(canvas, ctx, host);
      if (!spawned && now - t0 >= IMPACT_MS) {
        spawned = true;
        const cards = host.querySelectorAll(".used-card.is-fresh");
        cards.forEach((card, index) => {
          const rarity = rarities[index];
          if (rarity) sparks.push(...spawnCardEdgeSparks(relativeBox(card, canvas), rarity));
        });
      }
      const live = stepSlamSparks(sparks, dt);
      sparks.length = 0;
      sparks.push(...live);
      drawSlamSparks(ctx, w, h, sparks);
      if (!spawned || sparks.length > 0) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playKey, rarities]);

  if (!playKey) return null;
  return <canvas className="used-play-fx" ref={canvasRef} aria-hidden="true" />;
}
